import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Server, Globe, Lock, HelpCircle } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const hostingPlans = [
  {
    name: "Başlangıç",
    price: { monthly: 79, yearly: 59 },
    description: "Kişisel sitelere ve yan projelere uygun giriş paketi.",
    features: ["1 site", "20 GB NVMe depolama", "Sınırsız bandwidth", "Ücretsiz SSL", "Günlük yedek", "7/24 chat desteği"],
    cta: "Başla",
  },
  {
    name: "Business",
    price: { monthly: 199, yearly: 149 },
    description: "Büyüyen işletmeler için ihtiyaç duyduğunuz her şey.",
    popular: true,
    features: ["Sınırsız site", "100 GB NVMe depolama", "Öncelikli CDN", "1 yıl ücretsiz domain", "Saatlik yedekleme", "Staging ortamları", "Gelişmiş DDoS koruması", "Telefon desteği"],
    cta: "En Popüler",
  },
  {
    name: "Enterprise",
    price: { monthly: 499, yearly: 399 },
    description: "Ajanslar ve yüksek trafikli markalar için.",
    features: ["Sınırsız her şey", "500 GB NVMe depolama", "Dedicated IP", "Profesyonel migrasyon", "Saatlik yedekler", "Özel SLA (99,99)", "7/24 özel mühendis"],
    cta: "Satışla konuş",
  },
];

const vpsPlans = [
  { name: "VPS · Nano", price: { monthly: 149, yearly: 119 }, cpu: "2 vCPU", ram: "4 GB RAM", storage: "80 GB NVMe", bandwidth: "4 TB trafik" },
  { name: "VPS · Pro", price: { monthly: 399, yearly: 319 }, cpu: "4 vCPU", ram: "8 GB RAM", storage: "160 GB NVMe", bandwidth: "6 TB trafik", popular: true },
  { name: "VDS · Max", price: { monthly: 1299, yearly: 1049 }, cpu: "8 vCPU", ram: "32 GB RAM", storage: "500 GB NVMe", bandwidth: "10 TB trafik" },
];

const faqs = [
  { q: "Mevcut sitemi ücretsiz taşıyabilir misiniz?", a: "Evet. Migrasyon ekibimiz tüm taşımaları ücretsiz yapar — kesintiyi en aza indirecek şekilde zamanlama birlikte ayarlanır." },
  { q: "Paket limitini aşarsam ne olur?", a: "Herhangi bir ek ücret öncesi bildirim alırsınız. Asla askıya almayız veya kısıtlamayız — her zaman çevrimiçi kalırsınız." },
  { q: "İade garantisi var mı?", a: "Tüm paketler 30 gün koşulsuz iade garantisiyle gelir. Sözleşme yok, gizli ücret yok." },
  { q: "Hangi veri merkezlerinde çalışıyorsunuz?", a: "Kuzey Amerika, Avrupa, Asya-Pasifik, Güney Amerika ve Afrika genelinde 42 edge lokasyonunda hizmet veriyoruz." },
  { q: "Firewall gerçekten her pakette dahil mi?", a: "Evet. DDoS koruması ve Layer 7 WAF'ımız paylaşımlı hosting'ten dedicated sunuculara kadar tüm ürünlerimizde standarttır." },
];

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");

  return (
    <div data-testid="pricing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-50 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-96 radial-glow-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-16 lg:pt-24 lg:pb-20 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-5">Fiyatlandırma</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Basit paketler.<span className="text-zinc-500"> Sürpriz yok.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl mx-auto">
            Aylık veya yıllık %20 indirimli ödeme. İstediğiniz zaman yükseltin, düşürün veya iptal edin.
          </p>

          <div className="mt-10 inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/5">
            <button
              data-testid="billing-toggle-monthly"
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billing === "monthly" ? "bg-white text-[#0A0B10]" : "text-zinc-400 hover:text-white"}`}
            >
              Aylık
            </button>
            <button
              data-testid="billing-toggle-yearly"
              onClick={() => setBilling("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${billing === "yearly" ? "bg-white text-[#0A0B10]" : "text-zinc-400 hover:text-white"}`}
            >
              Yıllık
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">-%20</span>
            </button>
          </div>
        </div>
      </section>

      {/* HOSTING */}
      <section className="relative pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <Globe size={20} className="text-blue-400" />
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Web Hosting</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {hostingPlans.map((plan, i) => (
              <div
                key={plan.name}
                data-testid={`hosting-plan-${i}`}
                className={`relative rounded-3xl p-8 flex flex-col ${plan.popular ? "tracing-beam bg-gradient-to-b from-[#0F1424] to-[#0A0B10] border border-blue-500/30" : "glass card-hover"}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-semibold shadow-lg shadow-blue-500/30">
                    <Sparkles size={12} /> En Popüler
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="font-heading text-2xl font-semibold mb-2">{plan.name}</h3>
                  <p className="text-sm text-zinc-400">{plan.description}</p>
                </div>
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-bold tracking-tight">{plan.price[billing]} ₺</span>
                    <span className="text-zinc-500 text-sm">/ ay</span>
                  </div>
                  {billing === "yearly" && (
                    <p className="text-xs text-blue-400 mt-2">
                      Yıllık faturalanır — {(plan.price.monthly - plan.price.yearly) * 12} ₺ tasarruf
                    </p>
                  )}
                </div>
                <ul className="space-y-3 mb-10 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/iletisim"
                  data-testid={`hosting-cta-${i}`}
                  className={`btn-shine w-full text-center py-3.5 rounded-full text-sm font-semibold transition-all ${plan.popular ? "bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_30px_rgba(0,122,255,0.4)]" : "bg-white/5 hover:bg-white/10 border border-white/10 text-white"}`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VPS */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <Server size={20} className="text-blue-400" />
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">VPS / VDS Sunucular</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {vpsPlans.map((plan, i) => (
              <div
                key={plan.name}
                data-testid={`vps-plan-${i}`}
                className={`rounded-3xl p-8 flex flex-col ${plan.popular ? "tracing-beam bg-gradient-to-b from-[#0F1424] to-[#0A0B10] border border-blue-500/30 relative" : "glass card-hover"}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-semibold shadow-lg shadow-blue-500/30">
                    <Sparkles size={12} /> En Çok Tercih
                  </div>
                )}
                <h3 className="font-heading text-xl font-semibold mb-2">{plan.name}</h3>
                <div className="mb-7">
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-bold tracking-tight">{plan.price[billing]} ₺</span>
                    <span className="text-zinc-500 text-sm">/ ay</span>
                  </div>
                  {billing === "yearly" && (<p className="text-xs text-blue-400 mt-2">Yıllık faturalanır</p>)}
                </div>
                <ul className="space-y-3 mb-10 flex-1">
                  {[plan.cpu, plan.ram, plan.storage, plan.bandwidth, "Tam root erişimi", "Snapshot yedek"].map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-zinc-300">
                      <Check size={16} className="text-blue-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/iletisim"
                  data-testid={`vps-cta-${i}`}
                  className={`btn-shine w-full text-center py-3.5 rounded-full text-sm font-semibold transition-all ${plan.popular ? "bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_30px_rgba(0,122,255,0.4)]" : "bg-white/5 hover:bg-white/10 border border-white/10 text-white"}`}
                >
                  Hemen Al
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Firewall */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1220] to-[#0A0B10] p-10 lg:p-14">
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/5 px-3 py-1 mb-5">
                  <Lock size={14} className="text-blue-400" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">Güvenlik</span>
                </div>
                <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-5">Kurumsal Firewall & DDoS</h3>
                <p className="text-zinc-400 leading-relaxed mb-8">
                  Uyumluluk standartlarına özel koruma veya özel SLA mı gerekiyor? Güvenlik ekibimiz
                  stack'inize özel çözüm hazırlar — saatler içinde.
                </p>
                <Link to="/iletisim" data-testid="firewall-cta" className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-4 text-sm font-semibold text-white transition-all">
                  Teklif iste <ArrowRight size={15} />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { v: "1+ Tbps", l: "DDoS kapasitesi" },
                  { v: "< 2 sn", l: "Saldırı müdahale" },
                  { v: "ISO 27001", l: "Sertifikalı" },
                  { v: "SOC 2", l: "Type II hazır" },
                ].map((s) => (
                  <div key={s.l} className="glass rounded-2xl p-5">
                    <div className="font-heading text-2xl font-semibold">{s.v}</div>
                    <div className="text-xs text-zinc-500 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 border-t border-white/5 bg-[#07080C]/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <HelpCircle size={20} className="text-blue-400" />
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Sık sorulan sorular</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="glass rounded-2xl px-6 border-0">
                <AccordionTrigger className="text-left font-semibold hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
